import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { bandLabel } from "@/components/audit/AuditReports";
import { auditorLabel } from "@/components/audit/auditor-label";
import DismissedFindings from "@/components/audit/DismissedFindings";
import AuditBackLink from "@/components/audit/AuditBackLink";
import ReportMarkdown from "@/components/audit/ReportMarkdown";
import PageHeader from "@/components/shell/PageHeader";
import EmptyState from "@/components/ui/EmptyState";
import { Page, PageBody } from "@/components/ui/Page";
import { trpc } from "@/lib/trpc";
import { useWindowTitleDetail } from "@/lib/window-title";
import { workspaceModuleRedirect } from "@/lib/workspace-module-access";
import { useReloadForNewBuild } from "@/lib/stale-build";

// The Report behind a Score (user story 2): the Auditor's markdown as written,
// with the Score the block carried above it. The trailing underscore in the
// file name keeps this page out of the overview's layout.
export const Route = createFileRoute("/_authenticated/$orgSlug/audit_/reports/$reportId")({
  beforeLoad: async ({ params, context }) => {
    const viewer = await context.queryClient.ensureQueryData(
      trpc.organization.viewer.queryOptions(),
    );
    const target = workspaceModuleRedirect(viewer, params.orgSlug);
    if (target) throw redirect(target);
  },
  loader: ({ params, context }) =>
    context.queryClient.ensureQueryData(
      trpc.audit.report.queryOptions({ orgSlug: params.orgSlug, reportId: params.reportId }),
    ),
  errorComponent: ReportLoadError,
  component: ReportPage,
});

function ReportLoadError({ error }: { error: unknown }) {
  const { orgSlug } = Route.useParams();
  if (useReloadForNewBuild(error)) return null;
  return (
    <Page>
      <PageHeader title="Audit" back={<AuditBackLink list="reports" orgSlug={orgSlug} />} />
      <PageBody>
        <EmptyState title="This Report could not be opened.">
          {error instanceof Error ? error.message : String(error)}
        </EmptyState>
      </PageBody>
    </Page>
  );
}

function ReportPage() {
  const { orgSlug, reportId } = Route.useParams();
  const { data: report } = useSuspenseQuery(trpc.audit.report.queryOptions({ orgSlug, reportId }));
  const title = auditorLabel(report.auditor);
  useWindowTitleDetail(title);
  const scoreLine =
    report.scoreState === "scored" && report.score !== null
      ? `${report.score}% ${bandLabel(report.band)}`
      : report.scoreState === "not-scored"
        ? `Not scored${report.scoreNote ? `: ${report.scoreNote}` : ""}`
        : "No Score in the Report";
  // The header carries the facts (Auditor, Score, as-of day); the Report's
  // own first heading repeats them and is skipped.
  return (
    <Page>
      <PageHeader
        back={<AuditBackLink list="reports" orgSlug={orgSlug} accountId={report.accountId} />}
        title={title}
        context={`${scoreLine} · ${report.runAt.slice(0, 10)}`}
      />
      <PageBody>
        <ReportMarkdown markdown={report.content} skipLeadingHeading />
        <DismissedFindings orgSlug={orgSlug} accountId={report.accountId} />
      </PageBody>
    </Page>
  );
}
